import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface MinimaxT2aParams {
  /**
   * 语音模型名称，默认为 speech-2.5-hd-preview
   */
  model?: string;
  /**
   * 要转换为语音的文本内容，必填
   */
  text: string;
  /**
   * 是否启用流式输出，默认为 false
   */
  stream?: boolean;
  /**
   * 音频输出格式：url（返回音频文件URL）或 hex（返回十六进制音频数据），默认为 url
   * @values url | hex
   */
  output_format?: 'url' | 'hex';
  /**
   * 语音设置，包括语音ID、语速、音量、音调
   */
  voice_setting?: {
    /**
     * 语音ID，如 English_Graceful_Lady、Chinese_Female_01 等
     */
    voice_id?: string;
    /**
     * 语速，范围 0.5-2.0，默认为 1.0
     */
    speed?: number;
    /**
     * 音量，范围 0-10，默认为 1.0
     */
    vol?: number;
    /**
     * 音调，范围 -12 到 12，默认为 0
     */
    pitch?: number;
  };
  /**
   * 语言增强，如 zh（中文）、en（英文）等
   */
  language_boost?: string;
  /**
   * 音频设置
   */
  audio_setting?: Record<string, any>;
  /**
   * 发音字典，用于自定义特定词语的发音
   */
  pronunciation_dict?: Record<string, any>;
  /**
   * 语音变调设置
   */
  voice_modify?: Record<string, any>;
  /**
   * 是否启用字幕
   */
  subtitle_enable?: boolean;
  /**
   * 流式输出选项
   */
  stream_options?: Record<string, any>;
}

export interface MinimaxT2aResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * minimax_t2a
 *
 * MiniMax 文本转语音生成接口
 * 
 * Model: speech-2.5-hd-preview (默认模型)
 * Features: 文本转语音生成功能
 * Output Formats: url（返回音频文件URL）或 hex（返回十六进制音频数据）
 * Stream: 支持流式输出（用于实时语音生成）和非流式输出
 * Advanced: 支持多种语音ID选择、语速调节(0.5-2.0)、音量控制(0-10)、音调调整(-12到12)、语言增强、发音字典、语音变调
 * 
 * API Documentation: https://platform.minimax.io/docs/api-reference/speech-t2a-http
 *
 * @example
 * ```typescript
 * import { initSeacloud, minimaxT2a } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await minimaxT2a({
 *   // 参数见 MinimaxT2aParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 MinimaxT2aParams 接口定义
 * @returns 生成的资源列表
 */
export async function minimaxT2a(
  params: MinimaxT2aParams,
): Promise<MinimaxT2aResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'minimax_t2a',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: MinimaxT2aResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
